import React, { memo, useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';
import Add from '@mui/icons-material/Add';

import { CardWrapper, useHookForm } from '@monorepo/common';

import { ArrowRight as ArrowRightIcon } from '../../../../components/Icons/arrow-right';
import { IEventsStepProps } from '../../interfaces';
import { itemLocation } from '../staticValues';
import LocationTitle from '../../../../components/JobStepper/components/LocationTitle';
import { IStep2Form } from './formInterfaces';
import Location from './components/Location';
import Salary from './components/Salary';
import { schema } from './schema';

interface IStep3Props extends IEventsStepProps {
  defaultData: IStep2Form
  setStep2: (val: IStep2Form) => void
}

export const Step2 = memo(({ onNext, onBack, Nav, defaultData, setStep2 }:IStep3Props) => {
  const [isShowLocation, setIsShowLocation] = useState(false);
  const [changedFields, setChangedFields] = useState<IStep2Form | null>(null);
  const { register, errors, getValues, setValue, control, handleSubmit, useFieldArray, setFormData } = useHookForm<IStep2Form>({
    schema,
    showChangedFields: (values) => {
      setChangedFields(values);
    }
  });
  const goBack = () => {
    if (onBack) {
      if (changedFields && (JSON.stringify(defaultData).toString() !== JSON.stringify(changedFields))) {
        setStep2(changedFields);
      }
      onBack();
    }
  };

  const { fields, prepend, remove } = useFieldArray({
    name: 'locations',
    control
  });

  const increase = () => {
    prepend(itemLocation);
  };

  const decrease = (id: number) => {
    if (fields.length === 1) {
      setValue('locations', [itemLocation]);
      return;
    }
    remove(id);
  };
  const onSubmit = (data: IStep2Form) => {
    if (data && onNext) {
      // The "at least one location" rule now lives in the schema (locations.min(1)
      // when not remote), so a failure is blocked by handleSubmit and shown inline
      // below LocationTitle — matching the Post a Job flow instead of a toast.
      setStep2(data);
      onNext();
    }
  };

  // `status` is the "is this job remote?" answer, so the next isShowLocation is its
  // inverse. Derive both from the argument rather than reading the pre-toggle
  // isShowLocation, which is one render behind and only happened to be right
  // because the radio group fires exclusively on an actual change.
  const toggleLocation = (status: boolean) => {
    const showLocation = !status;
    setIsShowLocation(showLocation);
    if (showLocation) {
      increase();
    }
  };

  // MUI StepContent is unmountOnExit, so this re-seeds on every re-entry to the
  // step. `noneLocation` alone is the answer: a remote job is exactly the one with
  // no locations, so the old `|| !defaultData.locations.length` clause contradicted
  // itself and snapped the radio back to "No" on every return (D-24094 §12).
  // Matches components/JobStepper/steps/Step3/index.tsx.
  useEffect(() => {
    setIsShowLocation(!defaultData.noneLocation);
    const timeout = setTimeout(() => {
      defaultData.locations.forEach((item) => {
        prepend(item);
      });
      clearTimeout(timeout);
    }, 0);
    setFormData(defaultData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultData]);

  useEffect(() => {
    setValue('noneLocation', !isShowLocation);
    if (!isShowLocation) {
      setValue('locations', []);
    }
  }, [isShowLocation]);

  return (
    <CardWrapper
      scrollOptions={{
        isErrorsAutoScrollPage: errors
      }}
    >
      <form
        data-cy="step3"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Salary
          defaultValueMax={defaultData.salaryMax}
          defaultValueMin={defaultData.salaryMin}
          defaultValueExact={defaultData.salaryExact}
          defaultValueSalaryUnitId={defaultData.salaryUnitId}
          defaultValueRadio={defaultData.salaryUnitRadio}
          errors={errors}
          setValue={setValue}
          getValues={getValues}
          register={register}
        />

        <LocationTitle
          value={!isShowLocation}
          toggleLocation={toggleLocation}
        />

        {/* Array-level message from the schema's locations.min(1) rule. The rows
            below only render errors.locations[index], so this surfaces the
            "add at least one location" error inline instead of as a toast. */}
        {isShowLocation && errors?.locations?.message && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mb: 2 }}
          >
            {errors.locations.message}
          </Typography>
        )}

        {isShowLocation && (
        <>
          {fields.map((item, index) => (
            <Location
              key={item.id}
              register={register}
              fieldLength={fields.length || 0}
              pIsShowState={`locations.${index}.isShowState` as const}
              pAddress1={`locations.${index}.address1` as const}
              pAddress2={`locations.${index}.address2` as const}
              pCity={`locations.${index}.city` as const}
              pState={`locations.${index}.state` as const}
              pZip={`locations.${index}.zip` as const}
              pProvince={`locations.${index}.province` as const}
              pCountry={`locations.${index}.country` as const}
              decrease={decrease}
              id={index}
              setValue={setValue}
              errors={{
                ...(errors?.locations) && ({ ...errors.locations[index] })
              }}
              getValues={getValues}
            />
          ))}
        </>
        )}

        {isShowLocation && (
        <Button
          sx={{ mt: 2 }}
          type="button"
          variant="outlined"
          color="info"
          startIcon={<Add />}
          onClick={() => {
            increase();
          }}
        >
          Add an additional location
        </Button>
        )}
        <Nav
          nextDisabled={false}
          onBack={goBack}
          nextButton={
              (
                <Button
                  endIcon={(<ArrowRightIcon fontSize="small" />)}
                  variant="contained"
                  data-cy="nextStep"
                  disabled={false}
                  type="submit"
                >
                  Next Step
                </Button>
              )
            }
        />
      </form>
    </CardWrapper>
  );
});
